import { useEffect, useState } from 'react';

import useSetting from './useSetting';
import { settingKeys } from '../utils/settings';
import i18n from '../i18n';

// The click is there to be checked against rather than listened to, so it opens under the sound.
// Where it lands is kept, so a balance found in one preview holds in the others.
export const DEFAULT_CLICK_VOLUME = 0.3;
// A part's MIDI, in its own modal and played along with a chart: the same recording either way
export const DEFAULT_MELODY_VOLUME = 0.75;

// What the click slider's 100% is worth. The click peaks at twice its volume (CLICK_GAIN in
// metronomeClick.js) and skips the master limiter, so full on the raw scale drowns everything else.
const CLICK_MAX_LEVEL = 0.35;

// Only a wait still running after this is one the user is actually waiting on; a label or a ring
// that flashes past reads as a glitch
const DOWNLOAD_LABEL_DELAY_MS = 300;

/**
 * The click every preview plays against: on or off, the count-in, and how loud.
 * Shared by the chord-bar preview and the MIDI one, so both open the same way.
 */
const usePreviewMix = () => {
  const [metronome, setMetronome] = useState(true);
  const [countIn, setCountIn] = useState(true);
  const [clickVolume, setClickVolume] = useSetting(settingKeys.chordBarsClickVolume, DEFAULT_CLICK_VOLUME);

  return {
    metronome,
    setMetronome,
    countIn,
    setCountIn,
    clickVolume,
    setClickVolume,
    clickLevel: clickVolume * CLICK_MAX_LEVEL,
  };
};

/**
 * What the Play button says and shows while it is waiting, given the instruments it waits on.
 *
 * Play carries no caption in the header, so the words below are its tooltip and the ring around it
 * is what the wait actually looks like — `playBusy` turns the ring on, `playProgress` fills it
 * where the files being fetched can be counted and leaves it spinning where they can't.
 *
 * @param {Array<{samples: object, instrumentId: string}>} loaders - A useSampleLoader per instrument
 * @returns {{ playLabel: string, isFetchingSamples: boolean, playBusy: boolean,
 *   playProgress: ?number }}
 */
export const usePlayLabel = (loaders, { isLoading, isPlaying }) => {
  const isFetchingSamples = loaders.some(({ samples }) => samples.status === 'loading');
  // A decode counts as waiting too: the button is disabled either way, and the ring is there to say
  // why it can't be pressed rather than to report on downloads specifically
  const isWaiting = isFetchingSamples || isLoading;
  const [isSlowWait, setIsSlowWait] = useState(false);

  useEffect(() => {
    if (!isWaiting) {
      setIsSlowWait(false);

      return;
    }

    const timer = setTimeout(() => setIsSlowWait(true), DOWNLOAD_LABEL_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isWaiting]);

  // "Downloading" only when it is true: an instrument already on the device is read back, not fetched
  const isDownloading =
    isSlowWait &&
    loaders.some(
      ({ samples, instrumentId }) => samples.status === 'loading' && !samples.downloadedIds.has(instrumentId),
    );

  const playLabel = i18n.t(
    isDownloading ? 'player.downloading' : isLoading ? 'player.loading' : isPlaying ? 'player.stop' : 'player.play',
  );

  // Every loader still running, counted as one bar: two instruments fetched at once are one wait to
  // whoever pressed play. A total of 0 is a load that hasn't said how big it is yet, or a decode —
  // null, so the ring spins instead of claiming to be at 0%.
  const pending = loaders.filter(({ samples }) => samples.status === 'loading');
  const total = pending.reduce((sum, { samples }) => sum + (samples.total || 0), 0);
  const loaded = pending.reduce((sum, { samples }) => sum + (samples.loaded || 0), 0);

  return {
    playLabel,
    isFetchingSamples,
    playBusy: isSlowWait,
    playProgress: total > 0 ? loaded / total : null,
  };
};

export default usePreviewMix;
